import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Panel } from "@/components/studio/common";
import { api, eur, d, formatErr } from "@/lib/api";

const Row = ({ k, v, mono }) => (
  <div className="flex justify-between gap-4 py-1.5 text-sm border-b border-slate-50 last:border-0">
    <span className="text-slate-500">{k}</span><span className={`text-navy text-right break-all ${mono ? "font-mono text-xs" : ""}`}>{v || "—"}</span>
  </div>
);

export const ClienteCard = ({ p }) => {
  const c = p.cliente;
  return (
    <Panel title="Dati cliente" testid="pratica-cliente-card">
      <Row k="Nome" v={`${c.nome} ${c.cognome}`} />
      <Row k="Data di nascita" v={d(c.data_nascita)} />
      <Row k="Codice fiscale" v={c.codice_fiscale} mono />
      <Row k="Email" v={<a href={`mailto:${c.email}`} className="underline">{c.email}</a>} />
      <Row k="Telefono" v={<a href={`tel:${c.telefono}`} className="underline">{c.telefono}</a>} />
      <Row k="Consenso marketing" v={p.lead?.consenso_marketing ? "Sì" : "No"} />
      {p.mandato?.dati && <Row k="Residenza" v={`${p.mandato.dati.indirizzo}, ${p.mandato.dati.cap} ${p.mandato.dati.citta} (${p.mandato.dati.provincia})`} />}
      {p.mandato?.dati && <Row k="Qualità" v={p.mandato.dati.qualita === "erede" ? `Erede di ${p.mandato.dati.de_cuius_nome}` : "Titolare"} />}
    </Panel>
  );
};

export const ImportiCard = ({ p, patch, user }) => {
  const [f, setF] = useState({});
  useEffect(() => setF({ importo_stimato: p.importo_stimato ?? "", importo_effettivo: p.importo_effettivo ?? "", success_fee_pct: p.success_fee_pct ?? "" }), [p]);
  const locked = user.role === "segretaria";
  const num = (v) => (v === "" || v == null ? null : Number(v));
  const save = () => patch({ importo_stimato: num(f.importo_stimato), importo_effettivo: num(f.importo_effettivo), success_fee_pct: num(f.success_fee_pct) });
  return (
    <Panel title="Importi e compenso" testid="pratica-importi-card">
      <div className="grid grid-cols-3 gap-3">
        {[["importo_stimato", "Stimato €"], ["importo_effettivo", "Incassato €"], ["success_fee_pct", "Fee %"]].map(([k, l]) => (
          <div key={k}><Label className="text-xs text-slate-500">{l}</Label>
            <Input data-testid={`pratica-${k}-input`} type="number" step="0.01" disabled={locked} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-9" /></div>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="bg-navy text-white rounded-lg p-3"><p className="text-[11px] uppercase tracking-widest text-gold">Compenso studio</p><p className="font-serif text-2xl mt-1" data-testid="pratica-compenso">{eur(p.compenso)}</p></div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><p className="text-[11px] uppercase tracking-widest text-emerald-700">Quota cliente</p><p className="font-serif text-2xl mt-1 text-emerald-900" data-testid="pratica-quota-cliente">{eur(p.quota_cliente)}</p></div>
      </div>
      <div className="flex items-center justify-between mt-4 gap-3">
        <label className="flex items-center gap-2 text-sm"><Switch data-testid="pratica-kyc-switch" disabled={locked} checked={!!p.kyc_verificato} onCheckedChange={(v) => patch({ kyc_verificato: v })} />KYC / adeguata verifica</label>
        {!locked && <Button size="sm" data-testid="pratica-importi-save-btn" onClick={save} className="bg-navy hover:bg-navy-800">Salva importi</Button>}
      </div>
    </Panel>
  );
};

export const MatchCard = ({ p, setP }) => {
  const [busy, setBusy] = useState(false);
  const rematch = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/pratiche/${p.id}/rematch`); setP(data); toast.success("Matching rieseguito"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return (
    <Panel title="Verifica sul sito Consap" testid="pratica-match-card"
      action={<Button size="sm" variant="outline" data-testid="pratica-rematch-btn" onClick={rematch} disabled={busy}><RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />{busy ? "Ricerca su Consap…" : "Riesegui"}</Button>}>
      {p.match?.status === "unavailable" && <p data-testid="match-unavailable-msg" className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">Il sito Consap non era raggiungibile durante la verifica. Premi "Riesegui".</p>}
      {p.consap_records.length ? (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-wider"><tr>{["Nominativo", "Nascita", "Luogo", "Intermediario", "Rapporto", "Anno"].map((h) => <th key={h} className="text-left py-2 pr-3">{h}</th>)}</tr></thead>
          <tbody>{p.consap_records.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`consap-match-row-${r.id}`}>
              <td className="py-2 pr-3 font-medium">{r.cognome} {r.nome}</td><td className="pr-3">{d(r.data_nascita)}</td>
              <td className="pr-3">{r.luogo_nascita ? `${r.luogo_nascita} (${r.provincia_nascita})` : "—"}</td>
              <td className="pr-3">{r.banca}</td><td className="pr-3 font-mono text-xs">{r.rapporto}</td><td>{r.anno || "—"}</td>
            </tr>))}</tbody>
        </table>
        <p className="text-xs text-slate-500 mt-3">Consap non pubblica l'importo: inserisci l'importo stimato quando lo conosci.</p></div>
      ) : <p className="text-sm text-slate-500">Nessun rapporto trovato su Consap con stesso nominativo e data di nascita. Score migliore: {p.match?.score ?? 0}.</p>}
      <a href="https://consultaconti.consap.it/ricercaconto.aspx" target="_blank" rel="noreferrer" data-testid="consap-site-link" className="inline-block text-xs text-navy underline mt-2">Apri la ricerca sul sito Consap</a>
    </Panel>
  );
};
